import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  AuthenticateUser,
  AuthResponse,
  UserEndpoints,
} from '@/apis/users/requests'
import {
  REQUEST_STATUS,
  RequestError,
  RequestStatus,
} from '@/common/types_and_interfaces.ts'
import { INITIAL_ERROR } from '@/common/constants.ts'
import { RefreshToken } from '@/apis/users/requests'
import axios from 'axios'
import { AUTH_MODE, keycloakAuthService } from '@/auth/keycloak'

const MILI_SECONDS_PER_SECOND = 1000 as const
const BUFFER_SECONDS = 8 as const

type AuthRequestState = {
  status: RequestStatus
  error: RequestError
}

export type AuthSessionState = {
  request: AuthRequestState
  isAuthenticated: boolean
  require2FA: boolean
  phone: string
  remember_me: boolean
  user: UserData | null
  auth_badge: {
    access_token: string
    expires_at: number
  }
}

const InitialRequestState: AuthRequestState = {
  status: REQUEST_STATUS.IDLE,
  error: INITIAL_ERROR,
}

const InitialDataState: Omit<AuthSessionState, 'request'> = {
  isAuthenticated: false,
  require2FA: false,
  phone: '',
  remember_me: false,
  user: null,
  auth_badge: {
    access_token: '',
    expires_at: 0,
  },
} as const

const InitialState: AuthSessionState = {
  request: InitialRequestState,
  ...InitialDataState,
}

type AuthCreds = { email: string; password: string, remember_me: boolean }
export const POSTLogin = createAsyncThunk(
  UserEndpoints.login,
  async function (credentials: AuthCreds) {
    return await AuthenticateUser(credentials.email, credentials.password, credentials.remember_me) // Never rejects
  }
)

export const bootstrapFromKeycloak =  createAsyncThunk<
  AuthResponse,
  void,
  { rejectValue: string }
  >('auth/bootstrapFromKeycloak', async (_, { rejectWithValue }) => {
    try {
    if (AUTH_MODE !== 'keycloak') {
      return rejectWithValue('Not in keycloak mode')
    }

    await keycloakAuthService().ensureFreshToken(30)
    const token = keycloakAuthService().getAccessToken()
    const tokenParsed = keycloakAuthService().getTokenParsed()
    if (!token) return rejectWithValue('Missing KC token')

    const expMs = (tokenParsed?.exp ?? 0) * 1000
    const now = Date.now()
    const expires_in = Math.max(0, Math.floor((expMs - now) / 1000))

    return {
      expires_in: now + (expires_in - 8) * 1000,
      token_type: "",
      access_token: token
    }
  } catch (err) {
    return rejectWithValue('Unable to bootstrap from Keycloak')
  }
})


export const POSTRefreshToken = createAsyncThunk(
  UserEndpoints.refresh,
  async function () {
    return await RefreshToken()
  }
)
interface UserData {
  id: number
  status: number
  first_name: string | null
  last_name: string | null
  phone: string | null
  email: string | null
  date_of_birth: string | null
  city: string | null
  profile_picture: string | null
  language_preference_id: number | null
  global_timezone_id: number | null
  global_state_id: number | null
  global_country_id: number | null
  access_2fa: number | null
}

export const authSessionSlice = createSlice({
  name: 'AuthSession',
  initialState: InitialState,
  reducers: {
    Logout: () => {
      return InitialState
    },
    ResetSessionRequest: (state) => {
      return { ...state, request: InitialRequestState }
    },
    setRememberMe: (state, action: PayloadAction<boolean>) => {
      state.remember_me = action.payload
    },
    setVerified: (state) => {
      state.isAuthenticated = true
      state.require2FA = false
    },
    setUserData: (state, action: PayloadAction<UserData>) => {
      state.user = action.payload // Actualiza los datos del usuario
    },
  },
  extraReducers: function (builder) {
    builder
      .addCase(POSTLogin.pending, function (state) {
        state.request.status = REQUEST_STATUS.IN_FLIGHT
      })
      .addCase(POSTLogin.fulfilled, function (state, action) {
        if (action.payload.data) {
          const expiresIn = action.payload.data.expires_in
          const newExpiresAt = Date.now() + (expiresIn - BUFFER_SECONDS) * MILI_SECONDS_PER_SECOND
          state.auth_badge = {
            access_token: action.payload.data.access_token,
            expires_at: newExpiresAt,
          }
          state.isAuthenticated = true

          // Two FA is not needed in the login flow at this time (Amadeus)
          if(action.payload.data?.message == '2FA required' && action.payload.data?.phone != undefined){
            state.isAuthenticated = false
            state.require2FA = true
            state.phone = action.payload.data.phone
          }
          state.request.status = REQUEST_STATUS.SUCCESS
          state.request.error = INITIAL_ERROR //Resets Error Properties
          return
        }
        return {
          ...InitialDataState,
          request: {
            error: action.payload.error,
            status: REQUEST_STATUS.FAILED,
          },
        }
      })
      .addCase(POSTRefreshToken.fulfilled, function (state, action) {
        if (action.payload.data) {
          const expiresIn = action.payload.data.expires_in
          const newExpiresAt = Date.now() + (expiresIn - BUFFER_SECONDS) * MILI_SECONDS_PER_SECOND
          state.auth_badge = {
            access_token: action.payload.data.access_token,
            expires_at: newExpiresAt,
          }
          state.isAuthenticated = true
          state.request.status = REQUEST_STATUS.SUCCESS
          state.request.error = INITIAL_ERROR
          return
        }
      })
      .addCase(bootstrapFromKeycloak.pending, (state) => {
        state.request.status = REQUEST_STATUS.IN_FLIGHT;
      })
      .addCase(bootstrapFromKeycloak.fulfilled, (state, action) => {
          state.auth_badge = {
            access_token: action.payload.access_token,
            expires_at: action.payload.expires_in,
          }
          state.isAuthenticated = true;
          state.request.status = REQUEST_STATUS.SUCCESS;
          state.request.error = INITIAL_ERROR;
      })
      .addCase(bootstrapFromKeycloak.rejected, (state, action) => {
          state.request.status = REQUEST_STATUS.FAILED;
          state.request.error =  {
            code: -3,
            message: action.payload,
            details: null,
          } as RequestError;
      })
  },
})

export default authSessionSlice.reducer
export const { Logout, ResetSessionRequest, setRememberMe, setVerified, setUserData } =
  authSessionSlice.actions

// Async thunk for refreshing the token
export const refreshMe = createAsyncThunk('auth/me', async () => {
  try {
    const userResponse = await axios.get(`/auth/me`)
    return {
      user: userResponse.data,
    }
  } catch (error) { /* empty */ }
})
